import React, { useCallback } from 'react';

import { useExportTemplate, useStatusTemplates } from 'Templates';

import { useChildComponent, useChooseFiles } from '@gogame/hooks-ts';
import {
  useScreenGo,
  useScreenTitle,
  useScreenDispatch,
  useScreenState,
  ScreenLink,
} from '@gogame/screens';

import InfluencerAddPromoCodeForm from 'scenes/Influencers/InfluencersCampaigns/components/InfluencerAddPromoCodeForm';
import {
  InfluencerCampaignFormCreate,
  InfluencerCampaignFormDuplicate,
  InfluencerCampaignFormEdit,
} from 'scenes/Influencers/InfluencersCampaigns/components/InfluencerCampaignForm';
import RewardPackages from 'scenes/Influencers/InfluencersCampaigns/components/RewardPackages';
import {
  COLUMN_ID,
  COLUMN_GG_CAMPAIGNS,
  COLUMN_PERIOD,
  COLUMN_NAME,
  COLUMN_MASTER_CAMPAIGN,
  COLUMN_AGENCY,
  COLUMN_INFLUENCER_CHANNEL,
  COLUMN_APPLICATION,
  COLUMN_NETWORK,
  COLUMN_PLATFORMS,
  COLUMN_COUNTRY,
  COLUMN_COST_MODEL,
  COLUMN_PAYOUT_RATE,
  COLUMN_LAST_UPDATED_BY,
  COLUMN_LAST_UPDATED_DATE,
  COLUMN_CREATED_BY,
  COLUMN_CREATED_DATE,
  TEMPLATE_UPDATE_INFLUENCER_CAMPAIGN,
} from 'scenes/Influencers/InfluencersCampaigns/constants';
import {
  useExportInfluencerCampaignTemplate,
  useExportInfluencerCampaignSpendLog,
  useLoadInfluencersCampaignTable,
  useUploadGGCampaigns,
  useUploadUpdatedCampaigns,
} from 'scenes/Influencers/InfluencersCampaigns/data';
import {
  SET_TABLE_SETTINGS,
  COLLAPSE_COLUMNS,
  REORDER_COLUMNS,
} from 'scenes/Influencers/InfluencersCampaigns/screenReducer';
import InfluencerChannelDetails from 'scenes/Influencers/InfluencersChannels/components/InfluencerChannelDetails';
import MasterCampaignDetails from 'scenes/Influencers/MasterCampaigns/components/MasterCampaignDetails';
import AffiliateDetails from 'scenes/Affiliates/components/AffiliateDetails';

import { influencersCampaignsDetailsLink } from 'scenes/Influencers/InfluencersCampaigns/navigation';

import InfluencerCampaignActions from '../Actions';
import DataGrid from '@gogame/data-grid';
import Toolbar from './Toolbar';

import InfluencerCampaignsBulkActions from './InfluencerCampaignsBulkActions';
// TODO: uncomment when backend to tracking link is implemented
// import InfluencerAddParamsToLinkForm from 'scenes/Influencers/InfluencersCampaigns/components/InfluencerAddParamsToLinkForm';

const DetailsLink = ({ value, row }) => {
  return (
    <ScreenLink
      link={influencersCampaignsDetailsLink}
      pathParams={{ id: row.id }}
    >
      {value}
    </ScreenLink>
  );
};

const InfluencerCampaignDataTable = (_props) => {
  const [screenTitle] = useScreenTitle();
  const dispatch = useScreenDispatch();
  const screenState = useScreenState();
  const [childComponent, openChildComponent] = useChildComponent();

  const { filters, tableSettings, searchQuery, ready } = screenState;

  const { data, isLoading, isRefetching } = useLoadInfluencersCampaignTable(
    {
      filters,
      searchQuery,
      tableSettings,
    },
    {
      enabled: ready,
    }
  );

  const exportInfluencerCampaignTemplate =
    useExportInfluencerCampaignTemplate();
  const exportInfluencerCampaignSpendLog =
    useExportInfluencerCampaignSpendLog();

  const go = useScreenGo();
  const chooseFiles = useChooseFiles({ accept: '.csv' });

  const [exportUpdatedCampaignsTemplate] = useExportTemplate({
    name: TEMPLATE_UPDATE_INFLUENCER_CAMPAIGN,
  });

  const [uploadGGCampaigns] = useUploadGGCampaigns();
  const [uploadUpdatedCampaigns] = useUploadUpdatedCampaigns();

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleEdit = ({ id }) => {
    openChildComponent(InfluencerCampaignFormEdit, { id });
  };

  const handleDuplicate = ({ id }) => {
    openChildComponent(InfluencerCampaignFormDuplicate, { id });
  };

  const handleOpenRewardPackages = () => {
    openChildComponent(RewardPackages);
  };

  const handleOpenAffiliate = (_e, { id }) => {
    openChildComponent(AffiliateDetails, { id });
  };

  const handleOpenMasterCampaign = (_e, { id }) => {
    openChildComponent(MasterCampaignDetails, { id });
  };

  const handleOpenInfluencerChannel = (_e, { id }) => {
    openChildComponent(InfluencerChannelDetails, { id });
  };

  const handleExportTemplate = async () => {
    const { period, agencyIds, masterCampaignIds } = screenState.filters;
    await exportInfluencerCampaignTemplate({
      period,
      agencyIds,
      masterCampaignIds,
    });
  };

  const handleExportSpendLog = async () => {
    await exportInfluencerCampaignSpendLog({
      ids: data?.rows.map(({ id }) => id),
    });
  };

  const handleAddPromoCode = useCallback(
    (selectedRows) => {
      openChildComponent(InfluencerAddPromoCodeForm, {
        initialValues: {
          ids: selectedRows.map(({ id }) => id),
          names: selectedRows.map(({ name }) => name),
        },
        // onSubmit: loadData,
      });
    },
    [openChildComponent, InfluencerAddPromoCodeForm]
  );

  // TODO: uncomment when backend to tracking link is implemented

  // const handleAddParamsToTrackingLink = useCallback(
  //   selectedRows => {
  //     openChildComponent(InfluencerAddParamsToLinkForm, {
  //       initialValues: {
  //         ids: selectedRows.map(({ id }) => id),
  //       },
  //       onSubmit: loadData,
  //     });
  //   },
  //   [openChildComponent, loadData]
  // );

  const handleCreate = () => {
    openChildComponent(InfluencerCampaignFormCreate, {
      onSubmit: (response) => {
        go(response.id);
      },
    });
  };

  const handleUploadGGCampaigns = async () => {
    const [file] = await chooseFiles();
    await uploadGGCampaigns(file);
  };

  const handleUploadUpdatedCampaigns = async () => {
    const [file] = await chooseFiles();
    await uploadUpdatedCampaigns(file);
  };

  const handleExportUpdatedCampaigns = () => {
    exportUpdatedCampaignsTemplate();
  };

  const templateUpdateUpdated = useStatusTemplates({
    name: TEMPLATE_UPDATE_INFLUENCER_CAMPAIGN,
  });
  const selectedRowsToolbarActions = ({ selectedValues, reset }) => {
    return (
      <InfluencerCampaignsBulkActions
        reset={reset}
        onAddPromoCode={handleAddPromoCode}
        rowsRows={selectedValues.map((id) =>
          data?.rows?.find((row) => row.id === id)
        )}
      />
    );
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  return (
    <React.Fragment>
      <DataGrid
        selectedRowsToolbarActions={selectedRowsToolbarActions}
        checkboxSelection={true}
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={isLoading}
        fetching={isRefetching}
        ready={screenState.ready}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={
          <Toolbar
            onCreate={handleCreate}
            onExportSpendLog={handleExportSpendLog}
            onExportTemplate={handleExportTemplate}
            onUploadGGCampaigns={handleUploadGGCampaigns}
            onUploadUpdatedCampaigns={handleUploadUpdatedCampaigns}
            onExportUpdatedCampaigns={handleExportUpdatedCampaigns}
            onOpenRewardPackages={handleOpenRewardPackages}
            templateUpdateUpdated={templateUpdateUpdated}
          />
        }
        storageKey={'influencersCampaigns'}
        defaultColumnWidth={120}
        filtered={!!screenState.searchQuery}
      >
        <DataGrid.Column
          field={COLUMN_ID}
          type={'text'}
          headerName={'ID'}
          width={75}
          fixed={true}
          resizable={false}
          align={'right'}
          headerAlign={'right'}
        />
        <DataGrid.Column
          field={COLUMN_NAME}
          headerName={'Name'}
          minWidth={210}
          fixed={true}
          resizable={false}
          flex={1}
          type={'link'}
          linkComponent={DetailsLink}
        />

        <DataGrid.Colum
          field={COLUMN_PERIOD}
          headerName={'Period'}
          width={92}
          fixed={true}
          resizable={false}
          type={'date'}
          template={'MONTH'}
          timeZone={'utc'}
        />
        <DataGrid.Colum
          field={COLUMN_GG_CAMPAIGNS}
          headerName={'GG IDs'}
          width={120}
          type={'commaList'}
          sortable={false}
          reorderable={false}
          getItemValue={({ id }) => id}
        />

        <DataGrid.Colum
          field={COLUMN_MASTER_CAMPAIGN}
          headerName={'Master Campaigns'}
          width={210}
          type={'link'}
          onLinkClick={handleOpenMasterCampaign}
        />
        <DataGrid.Colum
          field={COLUMN_AGENCY}
          headerName={'Agency'}
          width={210}
          type={'link'}
          onLinkClick={handleOpenAffiliate}
        />

        <DataGrid.Colum
          field={COLUMN_INFLUENCER_CHANNEL}
          headerName={'Influencer Channel'}
          width={210}
          type={'link'}
          onLinkClick={handleOpenInfluencerChannel}
        />
        <DataGrid.Column
          field={COLUMN_APPLICATION}
          headerName={'Application'}
          width={180}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_NETWORK}
          headerName={'Network'}
          width={148}
          type={'text'}
          sortable={false}
        />

        <DataGrid.Column
          field={COLUMN_PLATFORMS}
          headerName={'Platforms'}
          width={148}
          type={'commaList'}
          sortable={false}
        />
        <DataGrid.Column
          field={COLUMN_COUNTRY}
          headerName={'Country'}
          width={148}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_COST_MODEL}
          headerName={'Cost Model'}
          width={112}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_PAYOUT_RATE}
          headerName={'Payout / Rate'}
          width={120}
          type={'currency'}
        />
        <DataGrid.Column
          field={COLUMN_LAST_UPDATED_BY}
          headerName={'Last updated by'}
          width={170}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_LAST_UPDATED_DATE}
          headerName={'Last updated'}
          width={135}
          type={'date'}
          template={'LLL'}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_BY}
          headerName={'Created by'}
          width={170}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_CREATED_DATE}
          headerName={'Created'}
          width={135}
          type={'date'}
          template={'LLL'}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          onEdit={handleEdit}
          onDuplicate={handleDuplicate}
          actionsComponent={InfluencerCampaignActions}
          limit={3}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};

export default InfluencerCampaignDataTable;
